import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';

/**
 * Button primitive — Design System v1 §8.1/§8.2.
 *
 * shadcn/ui cva implementation on Kyck tokens:
 *  - default     → Kyck-green primary CTA (Save / Send / Create)
 *  - secondary   → subtle filled (slate-100)
 *  - outline     → bordered, transparent fill
 *  - ghost       → transparent, subtle tinted hover (icon / list actions)
 *  - destructive → rose (permanent delete)
 *  - link        → text link
 * Sizes per §8.2: sm 32px · default 40px · lg 44px · icon (square).
 *
 * NOTE (Phase 2 Sub-phase B bridge): this file is still named `Button.tsx`
 * (capital) so the ~70 existing importers keep resolving while call sites are
 * migrated module-by-module. Once every call site uses an explicit canonical
 * variant, the file is renamed to the shadcn-canonical lowercase `button.tsx`
 * and the import paths updated (final Sub-phase B commit).
 */
const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground hover:bg-kyck-600',
        destructive:
          'bg-destructive text-destructive-foreground hover:bg-destructive/90',
        outline:
          'border border-input bg-background hover:bg-accent hover:text-accent-foreground',
        secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-10 px-4 py-2',
        sm: 'h-8 rounded-md px-3.5 text-sm',
        lg: 'h-11 rounded-md px-5',
        icon: 'h-10 w-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

type ButtonProps = React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  };

function Button({ className, variant, size, asChild = false, ...props }: ButtonProps) {
  const Comp = asChild ? Slot : 'button';
  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  );
}

export { Button, buttonVariants };
export type { ButtonProps };
